import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getApiBaseUrl } from '@/lib/capacitor';
import {
  ArrowLeft,
  Image,
  Film,
  Type,
  Mic,
  Eye,
  Clock,
  Play,
  Archive,
  Trash2,
  AlertCircle,
  MoreVertical,
  CheckCircle,
  X,
  ClipboardList,
  MessageCircle,
  Send,
  Download,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { VoiceNotePlayer } from '@/components/VoiceNotePlayer';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { formatDate } from '@/lib/utils';
import { toast } from '@/components/ui/toaster';

interface Post {
  _id: string;
  state: string;
  contentType: 'photo' | 'video' | 'hudText' | 'voiceNote';
  caption?: string;
  hudText?: string;
  originalFilename?: string;
  processedFilename?: string;
  voiceNoteFilename?: string;
  viewCount: number;
  errorDetails?: string;
  user: {
    name: string;
    email?: string;
  };
  createdAt: string;
  lastPublishedAt?: string;
}

interface MediaPreview {
  type: 'photo' | 'video';
  url: string;
}

interface Gallery {
  _id: string;
  name: string;
  mqttTopic: string;
}

const contentTypeIcons = {
  photo: Image,
  video: Film,
  hudText: Type,
  voiceNote: Mic,
};

const stateColors: Record<string, string> = {
  uploading: 'bg-gray-500',
  uploaded: 'bg-gray-500',
  'waiting-for-processing': 'bg-purple-500',
  processing: 'bg-purple-500 animate-pulse',
  processed: 'bg-blue-500',
  unread: 'bg-amber-500',
  published: 'bg-emerald-500',
  read: 'bg-blue-500',
  archived: 'bg-gray-500',
  'processing-failed': 'bg-red-500',
  'publish-failed': 'bg-red-500',
};

function isHeicFilename(filename?: string): boolean {
  return /\.(heic|heif)$/i.test(filename || '');
}

export default function ManageEntriesPage() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading, authenticatedFetch } = useAuth();
  
  const [posts, setPosts] = useState<Post[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<Post | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [mediaPreview, setMediaPreview] = useState<MediaPreview | null>(null);
  const [downloadingPostId, setDownloadingPostId] = useState<string | null>(null);
  
  // React dialog state
  const [reactTarget, setReactTarget] = useState<Post | null>(null);
  const [reactionText, setReactionText] = useState('');
  const [isSendingReaction, setIsSendingReaction] = useState(false);

  // Gallery dialog state
  const [galleryTarget, setGalleryTarget] = useState<Post | null>(null);
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [selectedGalleries, setSelectedGalleries] = useState<string[]>([]);
  const [isSendingToGallery, setIsSendingToGallery] = useState(false);

  // Processing progress state
  const [processingProgress, setProcessingProgress] = useState<Map<string, { percent: number; time?: string }>>(new Map());
  
  // Check authentication - redirect to login if not authenticated with Google as admin
  useEffect(() => {
    if (!authLoading && (!user || user.authType !== 'google' || user.role !== 'admin')) {
      navigate('/login?redirect=/admin/entries');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    fetchPosts();
  }, []);

  // Listen for processing progress events to update UI
  useEffect(() => {
    const handleProcessingProgress = (event: Event) => {
      const customEvent = event as CustomEvent<{ postId: string; percent: number; time?: string }>;
      const { postId, percent, time } = customEvent.detail;
      
      setProcessingProgress(prev => {
        const next = new Map(prev);
        next.set(postId, { percent, time });
        return next;
      });
    };

    window.addEventListener('ping-processing-progress', handleProcessingProgress);
    return () => {
      window.removeEventListener('ping-processing-progress', handleProcessingProgress);
    };
  }, []);

  // Listen for processing complete events to refresh the list
  useEffect(() => {
    const handleProcessingComplete = (event: Event) => {
      const customEvent = event as CustomEvent<{ postId: string; success: boolean }>;
      console.log('[ManageEntriesPage] Processing complete event received, refreshing posts');
      
      // Clear progress for this post
      setProcessingProgress(prev => {
        const next = new Map(prev);
        next.delete(customEvent.detail.postId);
        return next;
      });
      
      fetchPosts();
    };

    window.addEventListener('ping-processing-complete', handleProcessingComplete);
    return () => {
      window.removeEventListener('ping-processing-complete', handleProcessingComplete);
    };
  }, []);

  // Handle back button to close media preview overlay instead of navigating
  useEffect(() => {
    if (mediaPreview) {
      // Push history entry when overlay opens
      window.history.pushState({ mediaPreview: true }, '');
      
      const handlePopState = () => {
        setMediaPreview(null);
      };
      
      window.addEventListener('popstate', handlePopState);
      
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [mediaPreview]);

  const fetchPosts = async () => {
    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/posts?includeArchived=true`);
      const data = await response.json();
      if (data.success) {
        setPosts(data.posts);
      }
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReplay = async (postId: string) => {
    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/posts/${postId}/replay`, {
        method: 'POST',
      });
      if (response.ok) {
        toast({ title: 'Post replayed', description: 'Content sent to display' });
        fetchPosts();
      } else {
        throw new Error('Replay failed');
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to replay post', variant: 'destructive' });
    }
  };

  const handleMarkAsRead = async (postId: string) => {
    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/posts/${postId}/read`, {
        method: 'PATCH',
      });
      if (response.ok) {
        toast({ title: 'Marked as read' });
        fetchPosts();
      } else {
        throw new Error('Mark as read failed');
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to mark as read', variant: 'destructive' });
    }
  };

  const handleArchive = async (postId: string) => {
    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/posts/${postId}/archive`, {
        method: 'PATCH',
      });
      if (response.ok) {
        toast({ title: 'Post archived' });
        fetchPosts();
      } else {
        throw new Error('Archive failed');
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to archive post', variant: 'destructive' });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    setIsDeleting(true);
    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/posts/${deleteTarget._id}`, {
        method: 'DELETE',
      });
      if (response.ok) {
        toast({ title: 'Post deleted' });
        fetchPosts();
        setDeleteTarget(null);
      } else {
        throw new Error('Delete failed');
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete post', variant: 'destructive' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownloadOriginal = async (post: Post) => {
    if (!post.originalFilename || !['photo', 'video'].includes(post.contentType)) return;

    setDownloadingPostId(post._id);
    try {
      const response = await authenticatedFetch(
        `${getApiBaseUrl()}/api/posts/${post._id}/original/download`
      );

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.error || 'Failed to download original file');
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const extension = post.originalFilename.match(/\.[^.]+$/)?.[0] || '';
      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = `ping-original-${post._id}${extension}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);

      toast({ title: 'Download started', description: 'Downloading the original file' });
    } catch (error) {
      toast({
        title: 'Download failed',
        description: error instanceof Error ? error.message : 'Failed to download original file',
        variant: 'destructive',
      });
    } finally {
      setDownloadingPostId(null);
    }
  };

  const handleSendReaction = async () => {
    if (!reactTarget || !reactionText.trim()) return;

    setIsSendingReaction(true);
    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/reactions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          postId: reactTarget._id,
          text: reactionText.trim(),
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: 'Reaction sent!',
          description: 'A notification has been sent to the user.',
        });
        setReactTarget(null);
        setReactionText('');
      } else {
        throw new Error(data.error || 'Failed to send reaction');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to send reaction',
        variant: 'destructive',
      });
    } finally {
      setIsSendingReaction(false);
    }
  };

  const fetchGalleries = async () => {
    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/galleries`);
      const data = await response.json();
      if (data.success) {
        setGalleries(data.galleries);
      }
    } catch (error) {
      console.error('Failed to fetch galleries:', error);
    }
  };

  const openGalleryDialog = async (post: Post) => {
    setGalleryTarget(post);
    setSelectedGalleries([]);
    await fetchGalleries();
  };

  const handleSendToGallery = async () => {
    if (!galleryTarget || selectedGalleries.length === 0) return;

    setIsSendingToGallery(true);
    try {
      const response = await authenticatedFetch(`${getApiBaseUrl()}/api/galleries/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          postId: galleryTarget._id,
          galleryIds: selectedGalleries,
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast({
          title: 'Sent to gallery!',
          description: data.message,
        });
        setGalleryTarget(null);
        setSelectedGalleries([]);
      } else {
        throw new Error(data.error || 'Failed to send to gallery');
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to send to gallery',
        variant: 'destructive',
      });
    } finally {
      setIsSendingToGallery(false);
    }
  };

  const toggleGallerySelection = (galleryId: string) => {
    setSelectedGalleries((prev) =>
      prev.includes(galleryId)
        ? prev.filter((id) => id !== galleryId)
        : [...prev, galleryId]
    );
  };

  const getMediaUrl = (filename?: string) => {
    if (!filename) return null;
    return `${getApiBaseUrl()}/uploads/${filename}`;
  };

  const canMarkAsRead = (state: string) => {
    return state !== 'read' && state !== 'archived' && !state.includes('failed');
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }
  
  // Don't render if not authenticated as admin (redirect will happen)
  if (!user || user.authType !== 'google' || user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Redirecting to login...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 glass border-b border-border">
        <div className="max-w-4xl mx-auto px-4 h-16 flex items-center gap-4">
          <Link to="/admin">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <ClipboardList className="w-5 h-5 text-primary" />
          <h1 className="font-display font-semibold flex-1">Manage Entries</h1>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto p-4 space-y-6">
        {/* Posts Section */}
        {posts.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No posts
          </div>
        ) : (
          posts.map((post) => {
            const Icon = contentTypeIcons[post.contentType];
            const mediaUrl = getMediaUrl(
              post.processedFilename || post.originalFilename
            );
            const canPreviewPhoto = Boolean(mediaUrl) && !(!post.processedFilename && isHeicFilename(post.originalFilename));
            const voiceNoteUrl = getMediaUrl(post.voiceNoteFilename);
            const isFailedState = post.state.includes('failed');
            const isProcessing = post.state === 'processing' || post.state === 'waiting-for-processing';
            const progress = processingProgress.get(post._id);

            return (
              <div
                key={post._id}
                className="rounded-xl border border-border bg-card overflow-hidden"
              >
                {/* Mobile layout: vertical stacking */}
                <div className="flex flex-col md:flex-row">
                  {/* Mobile header with actions dropdown */}
                  <div className="flex items-center justify-between p-3 border-b border-border md:hidden">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{post.user.name}</p>
                      <div className="flex items-center gap-1.5">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            stateColors[post.state] || 'bg-muted'
                          }`}
                        />
                        <span className="text-xs text-muted-foreground capitalize">
                          {isProcessing && progress 
                            ? `Processing ${Math.round(progress.percent)}%`
                            : post.state.replace('-', ' ')}
                        </span>
                      </div>
                    </div>
                    
                    {/* Mobile actions dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => handleReplay(post._id)}
                          disabled={isFailedState}
                        >
                          <Play className="w-4 h-4 mr-2" />
                          Replay
                        </DropdownMenuItem>
                        {canMarkAsRead(post.state) && (
                          <DropdownMenuItem onClick={() => handleMarkAsRead(post._id)}>
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Mark as Read
                          </DropdownMenuItem>
                        )}
                        {post.state !== 'archived' && (
                          <DropdownMenuItem onClick={() => handleArchive(post._id)}>
                            <Archive className="w-4 h-4 mr-2" />
                            Archive
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem onClick={() => setReactTarget(post)}>
                          <MessageCircle className="w-4 h-4 mr-2" />
                          React
                        </DropdownMenuItem>
                        {(post.contentType === 'photo' || post.contentType === 'video') && (
                          <DropdownMenuItem
                            onClick={() => handleDownloadOriginal(post)}
                            disabled={downloadingPostId === post._id || !post.originalFilename}
                          >
                            <Download className="w-4 h-4 mr-2" />
                            {downloadingPostId === post._id ? 'Downloading...' : 'Download original'}
                          </DropdownMenuItem>
                        )}
                        {(post.contentType === 'photo' || post.contentType === 'video') && (
                          <DropdownMenuItem onClick={() => openGalleryDialog(post)}>
                            <Send className="w-4 h-4 mr-2" />
                            Send to Gallery
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          onClick={() => setDeleteTarget(post)}
                          className="text-destructive focus:text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  {/* Thumbnail - clickable for media preview */}
                  <div className="w-full h-48 md:w-32 md:h-32 flex-shrink-0 bg-muted">
                    {post.contentType === 'photo' && mediaUrl && canPreviewPhoto && (
                      <button
                        onClick={() => setMediaPreview({ type: 'photo', url: mediaUrl })}
                        className="w-full h-full focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset"
                      >
                        <img
                          src={mediaUrl}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                      </button>
                    )}
                    {post.contentType === 'photo' && (!mediaUrl || !canPreviewPhoto) && (
                      <div className="w-full h-full flex items-center justify-center">
                        <Image className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}
                    {post.contentType === 'video' && mediaUrl && (
                      <button
                        onClick={() => setMediaPreview({ type: 'video', url: mediaUrl })}
                        className="w-full h-full relative focus:outline-none focus:ring-2 focus:ring-primary focus:ring-inset group"
                      >
                        <video
                          src={mediaUrl}
                          className="w-full h-full object-cover"
                          muted
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
                          <Play className="w-10 h-10 text-white" />
                        </div>
                      </button>
                    )}
                    {(post.contentType === 'hudText' ||
                      post.contentType === 'voiceNote') && (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon className="w-8 h-8 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 p-4 space-y-2">
                    {/* Desktop: show name and state */}
                    <div className="hidden md:flex items-start justify-between gap-4">
                      <div>
                        <p className="font-medium">{post.user.name}</p>
                        {post.user.email && (
                          <p className="text-xs text-muted-foreground">
                            {post.user.email}
                          </p>
                        )}
                      </div>

                      {/* State badge with progress */}
                      <div className="flex items-center gap-2">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            stateColors[post.state] || 'bg-muted'
                          }`}
                        />
                        <span className="text-xs text-muted-foreground capitalize">
                          {isProcessing && progress 
                            ? `Processing ${Math.round(progress.percent)}%`
                            : post.state.replace('-', ' ')}
                        </span>
                        {/* Progress bar for processing */}
                        {isProcessing && progress && (
                          <div className="w-16 h-1.5 bg-purple-500/20 rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-purple-500 transition-all duration-300 ease-out"
                              style={{ width: `${Math.min(100, progress.percent)}%` }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Text */}
                    {(post.caption || post.hudText) && (
                      <p className="text-sm line-clamp-2">
                        {post.caption || post.hudText}
                      </p>
                    )}

                    {/* Voice note player */}
                    {voiceNoteUrl && (
                      <VoiceNotePlayer src={voiceNoteUrl} compact />
                    )}

                    {/* Error details */}
                    {isFailedState && post.errorDetails && (
                      <div className="flex items-start gap-2 p-2 rounded bg-destructive/10 border border-destructive/30">
                        <AlertCircle className="w-4 h-4 text-destructive flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-destructive font-mono">
                          {post.errorDetails}
                        </p>
                      </div>
                    )}

                    {/* Meta */}
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatDate(post.createdAt)}
                      </span>
                      <span className="flex items-center gap-1">
                        <Eye className="w-3 h-3" />
                        {post.viewCount} views
                      </span>
                      {post.lastPublishedAt && (
                        <span className="hidden md:inline">
                          Last published: {formatDate(post.lastPublishedAt)}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Desktop Actions */}
                  <div className="hidden md:flex p-4 flex-col gap-2 border-l border-border">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => handleReplay(post._id)}
                      disabled={isFailedState}
                    >
                      <Play className="w-3 h-3 mr-1" />
                      Replay
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => setReactTarget(post)}
                    >
                      <MessageCircle className="w-3 h-3 mr-1" />
                      React
                    </Button>
                    {(post.contentType === 'photo' || post.contentType === 'video') && (
                      <>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => handleDownloadOriginal(post)}
                          disabled={downloadingPostId === post._id || !post.originalFilename}
                        >
                          <Download className="w-3 h-3 mr-1" />
                          {downloadingPostId === post._id ? 'Downloading...' : 'Original'}
                        </Button>
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => openGalleryDialog(post)}
                        >
                          <Send className="w-3 h-3 mr-1" />
                          Gallery
                        </Button>
                      </>
                    )}
                    {canMarkAsRead(post.state) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleMarkAsRead(post._id)}
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Mark Read
                      </Button>
                    )}
                    {post.state !== 'archived' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleArchive(post._id)}
                      >
                        <Archive className="w-3 h-3 mr-1" />
                        Archive
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                      onClick={() => setDeleteTarget(post)}
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Post</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this post from {deleteTarget?.user.name}?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Media Preview Dialog */}
      <Dialog open={!!mediaPreview} onOpenChange={() => setMediaPreview(null)}>
        <DialogContent className="fixed inset-4 max-w-none left-0 top-0 flex items-center justify-center p-0 bg-black/95 border-none rounded-xl data-[state=open]:dialog-fullscreen-animate-in data-[state=closed]:dialog-fullscreen-animate-out">
          <button
            onClick={() => setMediaPreview(null)}
            className="absolute right-4 top-4 z-10 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          {mediaPreview?.type === 'photo' && (
            <img
              src={mediaPreview.url}
              alt=""
              className="max-w-full max-h-full object-contain"
            />
          )}
          {mediaPreview?.type === 'video' && (
            <video
              src={mediaPreview.url}
              className="max-w-full max-h-full"
              controls
              autoPlay
            />
          )}
        </DialogContent>
      </Dialog>

      {/* React Dialog */}
      <Dialog open={!!reactTarget} onOpenChange={() => {
        setReactTarget(null);
        setReactionText('');
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Reaction</DialogTitle>
            <DialogDescription>
              Send a reaction to {reactTarget?.user.name}'s post. They will receive a push notification.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label htmlFor="reaction" className="text-sm font-medium">
                Your reaction
              </label>
              <Textarea
                id="reaction"
                placeholder="Great photo! 👍 Love it!"
                value={reactionText}
                onChange={(e) => setReactionText(e.target.value)}
                disabled={isSendingReaction}
                rows={3}
                maxLength={500}
                autoFocus
              />
              <p className="text-xs text-muted-foreground text-right">
                {reactionText.length}/500
              </p>
            </div>
          </div>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" disabled={isSendingReaction}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={handleSendReaction}
              disabled={isSendingReaction || !reactionText.trim()}
            >
              {isSendingReaction ? 'Sending...' : 'Send Reaction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send to Gallery Dialog */}
      <Dialog open={!!galleryTarget} onOpenChange={() => {
        setGalleryTarget(null);
        setSelectedGalleries([]);
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send to Gallery</DialogTitle>
            <DialogDescription>
              Select one or more galleries to send this content to via MQTT.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {galleries.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                <p>No galleries configured yet.</p>
                <Link to="/admin/galleries" className="text-primary hover:underline">
                  Add a gallery
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {galleries.map((gallery) => (
                  <label
                    key={gallery._id}
                    className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted/50 cursor-pointer transition-colors"
                  >
                    <Checkbox
                      checked={selectedGalleries.includes(gallery._id)}
                      onCheckedChange={() => toggleGallerySelection(gallery._id)}
                      disabled={isSendingToGallery}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="font-medium">{gallery.name}</p>
                      <p className="text-xs text-muted-foreground font-mono truncate">
                        {gallery.mqttTopic}
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <DialogClose asChild>
              <Button variant="outline" disabled={isSendingToGallery}>
                Cancel
              </Button>
            </DialogClose>
            <Button
              onClick={handleSendToGallery}
              disabled={isSendingToGallery || selectedGalleries.length === 0}
            >
              {isSendingToGallery ? 'Sending...' : `Send to ${selectedGalleries.length || 0} Gallery${selectedGalleries.length !== 1 ? 'ies' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
